import React, { useEffect, useState } from 'react';
import { useParams, Navigate, useLocation } from 'react-router-dom';
import { api, SITE_URL } from '@/lib/api';
import { useSettings } from '@/lib/settings';
import SEO, { localBusinessSchema, organizationSchema } from '@/components/SEO';
import SectionRenderer from '@/components/sections/SectionRenderer';
import Breadcrumb from '@/components/Breadcrumb';
import { Skeleton } from '@/components/ui/skeleton';

export default function DynamicPage() {
    const params = useParams();
    const location = useLocation();
    const { settings } = useSettings();
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [redirect, setRedirect] = useState(null);

    const slug = params.slug || (location.pathname === '/' ? 'home' : location.pathname.replace(/^\//, '').replace(/\/$/, ''));

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        setPage(null);
        api.get(`/public/page/${slug}`)
            .then(({ data }) => {
                if (data && data._redirect) {
                    setRedirect(data._redirect);
                } else {
                    setPage(data);
                }
            })
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [slug]);

    if (redirect) {
        if (redirect.new_url.startsWith('http')) {
            window.location.href = redirect.new_url;
            return null;
        }
        return <Navigate to={redirect.new_url} replace />;
    }

    if (notFound) {
        return (
            <div className="sk-section">
                <div className="sk-container text-center max-w-2xl">
                    <SEO title="Stranica nije pronađena | Škobić Keramika" noindex={true} />
                    <p className="sk-kicker">404</p>
                    <h1 className="font-display text-4xl sm:text-5xl mt-3">Stranica nije pronađena</h1>
                    <p className="mt-4 text-foreground/70">Tražena stranica ne postoji ili je premještena.</p>
                </div>
            </div>
        );
    }

    if (loading || !page) {
        return (
            <div className="sk-section">
                <div className="sk-container space-y-4">
                    <Skeleton className="h-12 w-2/3" />
                    <Skeleton className="h-6 w-1/2" />
                    <Skeleton className="h-64 w-full" />
                </div>
            </div>
        );
    }

    const seo = page.seo || {};
    const base = (settings?.site_url || SITE_URL).replace(/\/$/, '');
    const canonical = seo.canonical_url || `${base}${page.slug === 'home' ? '' : `/${page.slug}`}`;
    const isHome = page.slug === 'home' || page.page_type === 'home';
    const schema = isHome
        ? { '@context': 'https://schema.org', '@graph': [localBusinessSchema(settings), organizationSchema(settings)] }
        : null;

    return (
        <>
            <SEO
                title={seo.meta_title || page.title}
                description={seo.meta_description || page.description}
                canonical={canonical}
                image={seo.og_image || page.featured_image}
                noindex={seo.robots_index === false}
                nofollow={seo.robots_follow === false}
                schema={schema}
            />
            {/* Optional custom CSS at page level (admin-only set) */}
            {page.custom_css && <style dangerouslySetInnerHTML={{ __html: page.custom_css }} />}
            {!isHome && (
                <div className="sk-container pt-6">
                    <Breadcrumb items={[{ name: page.title.split('–')[0].trim().split('|')[0].trim() || page.h1 || page.title, url: location.pathname }]} />
                </div>
            )}
            {(page.sections || [])
                .filter((s) => s.enabled !== false)
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((section, idx) => (
                    <SectionRenderer key={section.id || idx} section={section} index={idx} />
                ))}
        </>
    );
}
